import React from "react";
import { IoGiftOutline } from "react-icons/io5";
import { FiCheck } from "react-icons/fi";
import { TbArrowDownLeft, TbTrendingUp } from "react-icons/tb";
import { useQuery } from "@tanstack/react-query";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";

export type InviteMembership = {
  code: string;
  status: string;
  joinedAt: string;
  completedAt: string | null;
  pointsAwarded: number;
  requiredPairs: string[];
  rewards: {
    deposit: {
      points: number;
      minimumUSD: number;
      earned: boolean;
      earnedAt: string | null;
    };
    trade: {
      points: number;
      minimumUSD: number;
      earned: boolean;
      earnedAt: string | null;
    };
  };
};

type JoinedInviteCodesResponse = {
  status: string;
  data: {
    memberships: InviteMembership[];
  };
};

const TOTAL_CONDITIONS = 3;

export const useJoinedInviteCodes = (enabled = true) => {
  const axiosInstance = useAuthAxios();

  return useQuery({
    queryKey: ["invite-code-joined"],
    queryFn: async () => {
      const response = await axiosInstance.get<JoinedInviteCodesResponse>(
        API.getJoinedInviteCodes
      );
      return response.data;
    },
    retry: 1,
    enabled,
  });
};

export const getInProgressMembership = (
  memberships: InviteMembership[] = []
) =>
  memberships.find(
    (membership) => membership.status?.toUpperCase() === "IN_PROGRESS"
  );

const buildConditions = (membership: InviteMembership) => {
  const { deposit, trade } = membership.rewards;

  return [
    {
      id: "registration",
      title: "Registration",
      subtitle: "Completed",
      completed: true,
      points: null,
      icon: null,
    },
    {
      id: "deposit",
      title: "Deposit",
      subtitle: deposit.earned
        ? "Completed"
        : `Deposit min $${deposit.minimumUSD}`,
      completed: deposit.earned,
      points: deposit.earned ? null : deposit.points,
      icon: TbArrowDownLeft,
    },
    {
      id: "trade",
      title: "Trade",
      subtitle: trade.earned ? "Completed" : `Trade min $${trade.minimumUSD}`,
      completed: trade.earned,
      points: trade.earned ? null : trade.points,
      icon: TbTrendingUp,
    },
  ];
};

const getProgress = (membership: InviteMembership) => {
  const completedCount =
    1 +
    (membership.rewards.deposit.earned ? 1 : 0) +
    (membership.rewards.trade.earned ? 1 : 0);

  const totalPoints =
    membership.rewards.deposit.points + membership.rewards.trade.points;

  return {
    completedCount,
    percentComplete: Math.round((completedCount / TOTAL_CONDITIONS) * 100),
    conditionsRemaining: TOTAL_CONDITIONS - completedCount,
    earned: membership.pointsAwarded,
    total: totalPoints,
  };
};

const InviteCodeProgressCard = ({
  membership,
}: {
  membership: InviteMembership;
}) => {
  const conditions = buildConditions(membership);
  const { percentComplete, conditionsRemaining, earned, total } =
    getProgress(membership);

  return (
    <div className="rounded-2xl border border-[#B8DCF5] bg-[#F8FCFF] p-4 dark:border-[#1E3A5F]/50 dark:bg-[#0B121D]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4EBFD] dark:bg-[#162033]">
            <IoGiftOutline className="text-[20px] text-secondary_button" />
          </div>
          <div>
            <p className="text-[11px] text-[#4E606E] dark:text-[#9CA3AF]">
              Invite Code
            </p>
            <p className="text-[14px] font-bold text-[#13181B] dark:text-white">
              {membership.code}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-[#4E606E] dark:text-[#9CA3AF]">
            Earned
          </p>
          <p className="text-[18px] font-bold text-secondary_button">
            {earned}/{total} pts
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#D4EBFD] dark:bg-[#1F2937]">
          <div
            className="h-full rounded-full bg-secondary_button transition-all"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-[#4E606E] dark:text-[#9CA3AF]">
          <span>{percentComplete}% complete</span>
          <span>
            {conditionsRemaining} condition
            {conditionsRemaining === 1 ? "" : "s"} remaining
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {conditions.map((condition) => {
          const Icon = condition.icon;

          return (
            <div
              key={condition.id}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                {condition.completed ? (
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#26A17B]">
                    <FiCheck className="text-[18px] text-white" />
                  </div>
                ) : (
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E2EEF8] dark:bg-[#162033]">
                    {Icon && (
                      <Icon className="text-[18px] text-[#4E606E] dark:text-[#9CA3AF]" />
                    )}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#13181B] dark:text-white">
                    {condition.title}
                  </p>
                  <p
                    className={`text-[11px] ${
                      condition.completed
                        ? "text-[#26A17B]"
                        : "text-[#4E606E] dark:text-[#9CA3AF]"
                    }`}
                  >
                    {condition.subtitle}
                  </p>
                </div>
              </div>
              {condition.points !== null && (
                <p className="shrink-0 text-[13px] font-bold text-secondary_button">
                  +{condition.points} pts
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InviteCodeProgressCard;
